/**
 * Lesson templates. Each returns a `LessonConfig` describing the exercise;
 * the React view layer renders it. Kept as small pure functions so the lesson
 * definitions stay trivially testable.
 */
import type {
  ButtonLessonConfig,
  IdeConfig,
  PictureConfig,
  PictureSource,
  UnitTests,
} from "./lessonTypes";

/**
 * An IDE demo. The starter code defaults to `<world>.starter`, so most lessons
 * only need to name their world.
 */
export function IdeDemo(
  world: string,
  starterCode = `${world}.starter`,
): IdeConfig {
  return { type: "ide-demo", world, starterCode };
}

/**
 * A checked IDE lesson. The goal defaults to `<world>.goal`; pass a goal world
 * name to override it, or a full `UnitTests` map for multi-variant or
 * input/output lessons. The starter defaults to `<world>.starter`.
 */
export function IdeLesson(
  world: string,
  goal?: string | UnitTests,
  starterCode = `${world}.starter`,
  io = false,
): IdeConfig {
  const unitTests: UnitTests =
    goal === undefined
      ? { [world]: `${world}.goal` }
      : typeof goal === "string"
        ? { [world]: goal }
        : goal;
  return { type: "ide-lesson", world, starterCode, unitTests, io };
}

export function ButtonLesson(
  buttons: Record<string, boolean>,
  world: string,
  solution: string[],
): ButtonLessonConfig {
  return { type: "button", buttons, world, solution };
}

export function PictureDemo(
  images: PictureSource[],
  labels: string[],
): PictureConfig {
  return { type: "picture-demo", images, labels };
}

export function PictureLesson(
  images: PictureSource[],
  labels: string[],
): PictureConfig {
  return { type: "picture", images, labels };
}
